<template>
  <div class="vmd-upload-page">
    <!-- 初始化加载遮罩 -->
    <div v-if="initializing" class="init-loading">
      <div class="init-spinner">
        <i class="el-icon-loading" />
        <p>正在加载...</p>
      </div>
    </div>

    <template v-if="!initializing">
    <!-- 顶部标题栏 
    <div class="upload-header">
      <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
      <span class="title">门店图片上传</span>
    </div>
    -->
    <!-- 1. 身份自动识别区 -->
    <div class="section">
      <div class="section-title">
        <i class="el-icon-user"></i> 上传人信息
      </div>
      <div v-if="userInfo" class="identity-info">
        <el-tag type="success" size="small">姓名：{{ userInfo.userName }}</el-tag>
        <el-tag type="primary" size="small">工号：{{ userInfo.userCode }}</el-tag>
        <el-tag v-if="userInfo.userPhone" type="warning" size="small">手机：{{ userInfo.userPhone }}</el-tag>
        <el-tag type="info" size="small">部门：{{ userInfo.deptName }}</el-tag>
      </div>
      <div v-else class="identity-pending">
        <i v-if="detecting" class="el-icon-loading" />
        <span>{{ detecting ? '正在识别身份...' : '获取用户失败，不允许上传' }}</span>
      </div>
    </div>

    <!-- 2. 图片选择区 -->
    <div class="section" v-if="userInfo">
      <div class="section-title">
        <i class="el-icon-picture-outline"></i> 选择图片
      </div>
      <div class="select-area" @click="selectImages">
        <i class="el-icon-plus select-icon"></i>
        <p>点击选择照片（可多选）</p>
        <p class="hint">支持拍照或从相册选择</p>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          multiple
          style="display:none"
          @change="onFilesSelected"
        />
      </div>

      <!-- 图片预览 -->
      <div v-if="files.length > 0" class="preview-grid">
        <div v-for="(file, idx) in files" :key="idx" class="preview-item">
          <img :src="file.preview" :alt="file.name" />
          <span class="file-size">{{ formatSize(file.size) }}</span>
          <i class="el-icon-close remove-btn" @click.stop="removeFile(idx)"></i>
        </div>
      </div>
    </div>

    <!-- 3. 操作按钮 -->
    <div class="section" v-if="files.length > 0">
      <div class="btn-group">
        <el-button type="primary" @click="startUpload" :disabled="uploading || allDone" :loading="compressing">
          <i class="el-icon-upload2"></i> {{ compressing ? '压缩中...' : '开始上传' }}
        </el-button>
        <el-button v-if="uploading" @click="pauseAll">
          <i class="el-icon-video-pause"></i> 暂停
        </el-button>
        <el-button v-if="paused" type="warning" @click="resumeAll">
          <i class="el-icon-video-play"></i> 继续
        </el-button>
        <el-button type="danger" @click="cancelAll" :disabled="!uploading && !paused && !allDone">
          <i class="el-icon-delete"></i> 重置
        </el-button>
      </div>
    </div>

    <!-- 4. 上传进度 -->
    <div class="section" v-if="Object.keys(uploaders).length > 0">
      <div class="section-title">
        <i class="el-icon-loading" v-if="uploading"></i>
        <i class="el-icon-circle-check" v-else-if="allDone" style="color:#67c23a"></i>
        <span>上传进度</span>
        <span class="summary">{{ doneCount }}/{{ totalCount }}</span>
      </div>

      <div v-for="(item, key) in uploaders" :key="key" class="progress-item">
        <div class="progress-header">
          <span class="file-name">{{ key }}</span>
          <span v-if="item.status === 'completed'" class="status done">完成</span>
          <span v-else-if="item.status === 'uploading'" class="status uploading">上传中</span>
          <span v-else-if="item.status === 'failed'" class="status failed">失败</span>
          <span v-else class="status pending">等待</span>
        </div>
        <el-progress
          :percentage="item.percent"
          :status="item.status === 'completed' ? 'success' : item.status === 'failed' ? 'exception' : ''"
          :stroke-width="8"
        />
        <div class="progress-info">
          <span>{{ item.uploadedChunks }}/{{ item.totalChunks }} 块</span>
          <span v-if="item.speed">| {{ item.speed }} KB/s</span>
          <span v-if="item.remaining">| 剩余 {{ item.remaining }}</span>
        </div>
      </div>

      <!-- 网络状态 -->
      <div class="network-status" v-if="networkQuality">
        网络状态：<el-tag :type="networkTagType" size="mini">{{ networkQuality }}</el-tag>
      </div>
    </div>

    <!-- 5. 压缩进度弹层 -->
    <div class="compress-overlay" v-if="compressing">
      <div class="compress-box">
        <i class="el-icon-loading"></i>
        <p>正在压缩图片...</p>
        <p class="small">{{ compressInfo }}</p>
        <el-progress :percentage="compressPercent" :stroke-width="6" />
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import { ImageCompressor } from '@/utils/ImageCompressor'
import * as vmdApi from '@/api/web/vmd'

const compressor = new ImageCompressor()

export default {
  name: 'VmdUpload',

  data() {
    return {
      /* 身份 */
      userInfo: null,
      detecting: false,
      initializing: true,

      /* 文件 */
      files: [],
      uploaders: {},
      /** ★ 文件身份对照表：{ 原文件名: 唯一上传ID(UUID) }
       *  文件被选择后立即生成UUID，贯穿整个上传链路。
       *  同名文件每次选择生成不同UUID → 后端uploadId永不碰撞 → temp目录永不残留 */
      fileIdMap: {},

      /* 状态 */
      uploading: false,
      paused: false,
      compressing: false,
      compressInfo: '',
      compressPercent: 0,

      /* 网络 */
      networkQuality: '',

      /* 配置 */
      chunkSize: 30 * 1024, // 30KB 默认（手机端）
      concurrent: 3,
      maxRetries: 10,
      retryDelay: 2000
    }
  },

  computed: {
    totalCount() {
      return Object.keys(this.uploaders).length
    },
    doneCount() {
      return Object.values(this.uploaders).filter(u => u.status === 'completed').length
    },
    allDone() {
      if (this.totalCount === 0) return false
      return this.doneCount === this.totalCount
    },
    networkTagType() {
      const map = { '优秀': 'success', '良好': '', '一般': 'warning', '较差': 'danger' }
      return map[this.networkQuality] || 'info'
    }
  },

  mounted() {
    this.detectNetwork()
    this.checkPendingUploads()
    // 企业微信自动识别：检测URL中的code参数，自动解析当前用户
    this.autoDetectWxUser()
  },

  methods: {
    // ========== 企业微信自动识别身份 ==========
    /**
     * 企业微信自动识别用户身份
     *
     * 完整OAuth2流程：
     *   1. 页面加载时检测code（来自App.vue的OAuth2回调处理）
     *   2. 无code且在企业微信浏览器中 → 主动跳转OAuth2授权
     *   3. OAuth2回调 → code存入sessionStorage → App.vue恢复hash → 本页面再次加载
     *   4. 读取code → 调resolveWxUser解析身份 → 设置userInfo
     */
    async autoDetectWxUser() {
      this.detecting = true

      // Step 1: 优先从sessionStorage读取code
      let wxCode = sessionStorage.getItem('vmd_oauth_code')
      if (wxCode) {
        sessionStorage.removeItem('vmd_oauth_code')
      }

      // Step 2: 备选 — 从URL search参数读取
      if (!wxCode) {
        const searchMatch = window.location.search.match(/[?&]code=([^&]+)/)
        if (searchMatch) {
          wxCode = decodeURIComponent(searchMatch[1])
        }
      }

      // Step 3: 备选 — 从hash query参数读取
      if (!wxCode) {
        const hashMatch = window.location.hash.match(/[?&]code=([^&]+)/)
        if (hashMatch) {
          wxCode = decodeURIComponent(hashMatch[1])
        }
      }

      // Step 4: 有code → 调后端解析用户身份
      if (wxCode) {
        try {
          const wxRes = await vmdApi.resolveWxUser(wxCode)
          if (wxRes && wxRes.isNull && wxRes.name) {
            // resolveWxUser 只返回基础身份（name/code/phone），不包含部门信息
            // 需要额外调用 queryUser 获取完整信息（dept、path 等）
            const userId = wxRes.id
            try {
              const userRes = await vmdApi.queryUser(userId)
              if (userRes && userRes.name) {
                this.userInfo = {
                  userCode: userRes.id || userId || '',
                  userName: userRes.name || wxRes.name || '',
                  userPhone: userRes.phone || wxRes.phone || '',
                  deptName: userRes.dept || '',
                  storagePath: userRes.path || ''
                }
              } else {
                // queryUser 失败时，至少保留 resolveWxUser 的基础信息
                this.userInfo = {
                  userCode: wxRes.id || '',
                  userName: wxRes.name || '',
                  userPhone: wxRes.phone || '',
                  deptName: '',
                  storagePath: ''
                }
              }
            } catch (e2) {
              console.error('[VMD-Upload] queryUser补充信息失败:', e2)
              // 降级：仅用 resolveWxUser 的信息（无部门）
              this.userInfo = {
                userCode: wxRes.id || '',
                userName: wxRes.name || '',
                userPhone: wxRes.phone || '',
                deptName: '',
                storagePath: ''
              }
            }
          }
        } catch (e) {
          console.error('[VMD-Upload] resolveWxUser error:', e)
        }
        this.detecting = false
        this.initializing = false
        return
      }

      // Step 5: 无code → 检测企业微信环境，发起OAuth2静默授权
      const ua = navigator.userAgent.toLowerCase()
      const isWx = ua.includes('wxwork') || ua.includes('micromessenger')
      if (!isWx) {
        this.detecting = false
        this.initializing = false
        return
      }

      // 防止重复发起OAuth2
      if (sessionStorage.getItem('vmd_oauth_attempted')) {
        sessionStorage.removeItem('vmd_oauth_attempted')
        this.detecting = false
        this.initializing = false
        return
      }

      // 保存当前hash路径并跳转OAuth2
      const routePath = '/vmd/upload'
      sessionStorage.setItem('vmd_oauth_hash', '#/vmd/upload')
      sessionStorage.setItem('vmd_oauth_attempted', '1')

      const corpid = process.env.VUE_APP_WX_CORPID
      const agentId = process.env.VUE_APP_WX_AGENTID
      const redirectUri = encodeURIComponent(window.location.origin + '/')
      const nonce = Math.random().toString(36).substring(2, 10)
      const statePayload = routePath.replace(/^\//, '') + '__' + nonce
      const oauthUrl = 'https://open.weixin.qq.com/connect/oauth2/authorize'
        + `?appid=${corpid}`
        + `&redirect_uri=${redirectUri}`
        + '&response_type=code'
        + '&scope=snsapi_base'
        + `&state=${statePayload}`
        + `&agentid=${agentId}`
        + '#wechat_redirect'

      window.location.replace(oauthUrl)
    },

    // ========== 文件选择 ==========
    selectImages() {
      this.$refs.fileInput.click()
    },

    onFilesSelected(e) {
      const selectedFiles = Array.from(e.target.files)
      if (selectedFiles.length === 0) return
      selectedFiles.forEach(f => {
        const exists = this.files.some(ef => ef.name === f.name && ef.size === f.size)
        if (!exists) {
          f.preview = URL.createObjectURL(f)
          this.files.push(f)
          // ★ 文件选定后立即生成全新唯一上传ID（UUID），建立对照关系
          //    始终生成新UUID（覆盖fileIdMap旧值），确保每次选择都产生全新uploadId
          //    防止：上次上传已完成（completedUploads有旧UUID），不清空列表就重新选同名文件 → 复用旧UUID → 被后端拦截
          this.$set(this.fileIdMap, f.name, this._genUUID())
        }
      })
      e.target.value = ''
    },

    removeFile(idx) {
      const file = this.files[idx]
      if (file.preview) URL.revokeObjectURL(file.preview)
      this.files.splice(idx, 1)
      delete this.uploaders[file.name]
      // ★ 同步清理文件身份对照表
      this.$delete(this.fileIdMap, file.name)
    },

    formatSize(bytes) {
      if (bytes < 1024) return bytes + 'B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
      return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
    },

    // ========== 图片压缩 ==========
    async compressFiles() {
      this.compressing = true
      const compressed = []
      try {
        for (let i = 0; i < this.files.length; i++) {
          this.compressInfo = `${i + 1}/${this.files.length}: ${this.files[i].name}`
          this.compressPercent = Math.round((i / this.files.length) * 100)
          try {
            const cf = await compressor.compress(this.files[i])
            cf.preview = this.files[i].preview
            compressed.push(cf)
          } catch {
            compressed.push(this.files[i])
          }
        }
        this.files = compressed
      } finally {
        this.compressing = false
        this.compressInfo = ''
        this.compressPercent = 0
      }
    },

    // ========== 网络检测 ==========
    detectNetwork() {
      const conn = navigator.connection || navigator.webkitConnection
      const update = () => {
        if (conn && conn.downlink !== undefined) {
          const s = conn.downlink
          if (s >= 10) {
            this.chunkSize = 50 * 1024; this.concurrent = 8; this.maxRetries = 5; this.networkQuality = '优秀'
          } else if (s >= 5) {
            this.chunkSize = 30 * 1024; this.concurrent = 5; this.maxRetries = 8; this.networkQuality = '良好'
          } else if (s >= 2) {
            this.chunkSize = 20 * 1024; this.concurrent = 3; this.maxRetries = 12; this.networkQuality = '一般'
          } else {
            this.chunkSize = 10 * 1024; this.concurrent = 2; this.maxRetries = 15; this.networkQuality = '较差'
          }
        } else {
          this.networkQuality = '良好' // 默认
        }
      }
      update()
      if (conn) {
        conn.addEventListener('change', update)
        this._connHandler = () => conn.removeEventListener('change', update)
      }
    },

    // ========== 上传控制 ==========
    async startUpload() {
      if (!this.userInfo) {
        this.$message.error('获取用户失败，不允许上传')
        return
      }
      if (this.files.length === 0) {
        this.$message.warning('请选择图片')
        return
      }

      // 先压缩
      await this.compressFiles()

      this.uploading = true
      this.paused = false

      // 初始化每个文件的上传跟踪
      this.files.forEach(file => {
        if (!this.uploaders[file.name]) {
          this.$set(this.uploaders, file.name, {
            status: 'pending',
            percent: 0,
            uploadedChunks: 0,
            totalChunks: 0,
            speed: 0,
            remaining: '',
            // ★ 使用文件选择时预生成的UUID作为uploadId（确保每次上传唯一）
            uploadId: this.fileIdMap[file.name] || this._genUUID()
          })
        }
      })

      // 按文件大小从小到大排序上传
      const sorted = [...this.files].sort((a, b) => a.size - b.size)

      // ★ 第一轮：正常上传所有文件
      for (const file of sorted) {
        await this._uploadFile(file)
      }

      // ★ 第二轮：自动重试失败的文件（最多2轮）
      // 断点续传：先查询服务端已有分块，只补传缺失的（微信方案）
      const MAX_FILE_RETRIES = 2
      for (let retryRound = 0; retryRound < MAX_FILE_RETRIES; retryRound++) {
        const failedFiles = sorted.filter(f => {
          const u = this.uploaders[f.name]
          return u && u.status === 'failed'
        })
        if (failedFiles.length === 0) break

        console.log(`[VMD] 第${retryRound + 1}轮自动重试，${failedFiles.length} 个文件`)

        // ★ 改进：查询服务端已有分块，恢复localStorage记录，仅补传缺失块
        for (const file of failedFiles) {
          const u = this.uploaders[file.name]
          if (!u) continue
          u.status = 'pending'
          u.percent = 0
          u.uploadedChunks = 0
          this.clearSavedChunks(file.name)

          // 查询服务端已存在的分块
          try {
            const res = await vmdApi.checkChunks(u.uploadId, this.userInfo.storagePath)
            if (res && res.chunks && res.chunks.length > 0) {
              for (const idx of res.chunks) {
                this._markChunkDone(file.name, idx)
              }
              const saved = Math.round((res.chunks.length / Math.ceil(file.size / this.chunkSize)) * 100)
              u.percent = saved
              u.uploadedChunks = res.chunks.length
              console.log(`[VMD] 断点续传: ${file.name} 已有${res.chunks.length}块(${saved}%)，仅补传缺失块`)
            }
          } catch (e) {
            console.warn(`[VMD] checkChunks失败，将全量重传: ${file.name}`, e)
          }
        }

        for (const file of failedFiles) {
          if (!this.paused) await this._uploadFile(file)
        }
      }

      this.uploading = false

      const failed = Object.values(this.uploaders).filter(u => u.status === 'failed').length
      if (failed > 0) {
        this.$message.warning(`${failed} 个文件上传失败，请手动重试`)
      } else if (this.allDone) {
        this.$message.success('全部上传完成！')
        this.saveHistory()
      }
    },

    pauseAll() {
      this.paused = true
      this.uploading = false
      this.$message.info('已暂停上传')
    },

    resumeAll() {
      this.paused = false
      this.uploading = true
      // 重试未完成的文件
      const pending = this.files.filter(f => {
        const u = this.uploaders[f.name]
        return u && u.status !== 'completed'
      })
      this._resumeFiles(pending)
    },

    cancelAll() {
      this.uploading = false
      this.paused = false
      localStorage.removeItem('vmd_pending_uploads')
      this.files.forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview)
      })
      this.files = []
      this.uploaders = {}
      this.fileIdMap = {}  // ★ 同步清理文件身份对照表
      this.$message.info('已重置')
    },

    goBack() {
      if (this.uploading) {
        this.$confirm('正在上传中，确定要离开吗？', '提示', { type: 'warning' })
          .then(() => { this.pauseAll(); window.history.back() })
          .catch(() => {})
      } else {
        window.history.back()
      }
    },

    // ========== 上传文件核心逻辑 ==========
    async _uploadFile(file) {
      const u = this.uploaders[file.name]
      if (!u || this.paused) return

      // ★ 秒传：计算文件SHA-256，服务端已有则跳过上传（微信方案）
      const fileHash = await this._computeFileHash(file)
      if (fileHash) {
        try {
          const checkRes = await vmdApi.checkByMd5(fileHash)
          if (checkRes && checkRes.exists) {
            u.status = 'completed'
            u.percent = 100
            u.uploadedChunks = 0
            console.log(`[VMD] 秒传: ${file.name} 服务端已存在 (${fileHash.substring(0, 8)}...)，跳过上传`)
            return
          }
        } catch (e) {
          console.warn('[VMD] 秒传检查失败，继续正常上传:', e.message)
        }
      }

      u.status = 'uploading'
      const totalChunks = Math.ceil(file.size / this.chunkSize)
      u.totalChunks = totalChunks
      u.uploadedChunks = this._getSavedChunks(file.name).length
      u.percent = Math.round((u.uploadedChunks / totalChunks) * 100)

      let startTime = Date.now()
      let lastBytes = 0

      const uploadSingleChunk = async (idx) => {
        if (this.paused || this._isChunkDone(file.name, idx)) return true

        const start = idx * this.chunkSize
        const end = Math.min(start + this.chunkSize, file.size)
        const blob = file.slice(start, end)

        // 去重检查（★修复：request.js对VMD接口直接返回后端JSON，无.data包装层）
        try {
          const checkData = await vmdApi.checkChunk(u.uploadId, idx, this.userInfo.deptName)
          if (checkData && checkData.exists) {
            this._markChunkDone(file.name, idx, end - start)
            this._updateProgress(u, totalChunks, startTime, lastBytes, file.name)
            return true
          }
        } catch { /* 检查失败继续上传 */ }

        // 上传分块（带重试）
        let retries = 0
        while (retries <= this.maxRetries) {
          const formData = new FormData()
          formData.append('chunk', blob)
          formData.append('index', idx)
          formData.append('total', totalChunks)
          formData.append('filename', file.name)
          formData.append('uploadId', u.uploadId)
          formData.append('userCode', this.userInfo.userCode || '')
          formData.append('userName', this.userInfo.userName || '')
          formData.append('deptName', this.userInfo.deptName || '')
          formData.append('userPhone', this.userInfo.userPhone || '')
          formData.append('storagePath', this.userInfo.storagePath || '')

          try {
            // 使用封装的 vmdApi（内部通过 request 实例，Axios 会自动处理 baseURL 拼接，避免字符串拼接产生协议相对 URL）
            const res = await vmdApi.uploadChunk(formData)
            // ★ 必须检查后端业务返回值：success=false 说明服务端写入失败，不能标记为完成
            if (res && res.success === false) {
              throw new Error(res.message || '后端写入失败')
            }
            this._markChunkDone(file.name, idx, end - start)
            this._updateProgress(u, totalChunks, startTime, lastBytes, file.name)
            return true
          } catch (err) {
            retries++
            if (retries <= this.maxRetries) {
              // ★ 微信方案：指数退避，避免弱网下连续重试拥塞
              const delay = Math.min(this.retryDelay * Math.pow(2, retries - 1), 30000)
              await this._sleep(delay)
            }
          }
        }
        return false
      }

      // 并发上传分块
      const allChunks = []
      for (let i = 0; i < totalChunks; i++) {
        if (!this._isChunkDone(file.name, i)) {
          allChunks.push(i)
        }
      }

      let activeCount = 0
      let failedCount = 0
      let currentIndex = 0

      const processNext = () => {
        while (activeCount < this.concurrent && currentIndex < allChunks.length && !this.paused) {
          const idx = allChunks[currentIndex]
          currentIndex++
          activeCount++

          uploadSingleChunk(idx).then(success => {
            activeCount--
            if (!success) failedCount++
            processNext()
          })
        }

        if (currentIndex >= allChunks.length && activeCount === 0) {
          if (failedCount > 0 && !this.paused) {
            // 全部分块上传完毕但有失败：再清点一次
            const stillFailed = allChunks.filter(i => !this._isChunkDone(file.name, i))
            if (stillFailed.length > 0) {
              u.status = 'failed'
            } else {
              u.status = 'completed'
              u.percent = 100
              this.clearSavedChunks(file.name)
            }
          } else if (this.paused) {
            u.status = 'pending'
          } else {
            u.status = 'completed'
            u.percent = 100
            this.clearSavedChunks(file.name)
          }
        }
      }

      processNext()

      // 等待并发池排空
      while ((u.status === 'uploading') && !this.paused) {
        await this._sleep(500)
      }

      // ★ 闭环验证：通知服务端检查并合并（解决网络丢包导致的静默丢失）
      if (u.status === 'completed' && !this.paused) {
        const ncResult = await this._notifyComplete(file, u, totalChunks)
        if (!ncResult.success) {
          u.status = 'failed'
        }
      }
    },

    /**
     * ★ 闭环验证 — 通知服务端检查并合并分块（最多3轮补传）
     *
     * 在所有分块 HTTP 请求完成后调用。如果服务端发现缺失，精确补传后再试。
     * 解决网络层丢包导致的"静默丢失"问题。
     *
     * @param {File} file 原始文件对象
     * @param {Object} u 上传状态对象
     * @param {Number} totalChunks 总分块数
     * @returns {{success: boolean}}
     */
    async _notifyComplete(file, u, totalChunks) {
      const maxRounds = 3
      for (let round = 0; round < maxRounds; round++) {
        try {
          const res = await vmdApi.notifyComplete({
            uploadId: u.uploadId,
            total: totalChunks,
            filename: file.name,
            userCode: this.userInfo.userCode || '',
            userName: this.userInfo.userName || '',
            deptName: this.userInfo.deptName || '',
            userPhone: this.userInfo.userPhone || '',
            storagePath: this.userInfo.storagePath || ''
          })

          if (res && res.merged === true) {
            console.log(`[VMD] notifyComplete ✓ 合并成功: ${file.name}`)
            return { success: true }
          }

          if (res && res.missingChunks && res.missingChunks.length > 0) {
            const missing = res.missingChunks
            console.warn(`[VMD] notifyComplete 第${round + 1}轮补传 ${missing.length}块: ${JSON.stringify(missing)}`)

            // 精确补传缺失的分块
            for (const idx of missing) {
              const start = idx * this.chunkSize
              const end = Math.min(start + this.chunkSize, file.size)
              const blob = file.slice(start, end)
              const formData = new FormData()
              formData.append('chunk', blob)
              formData.append('index', idx)
              formData.append('total', totalChunks)
              formData.append('filename', file.name)
              formData.append('uploadId', u.uploadId)
              formData.append('userCode', this.userInfo.userCode || '')
              formData.append('userName', this.userInfo.userName || '')
              formData.append('deptName', this.userInfo.deptName || '')
              formData.append('userPhone', this.userInfo.userPhone || '')
              formData.append('storagePath', this.userInfo.storagePath || '')
              try {
                await vmdApi.uploadChunk(formData)
              } catch (e) {
                console.error(`[VMD] 补传分块${idx}失败: ${e.message}`)
              }
            }
            continue // 补传完再调用 notifyComplete
          }

          // 其他非预期响应
          console.error(`[VMD] notifyComplete ✗ 失败: ${res && res.message || '未知错误'}`)
          return { success: false }

        } catch (e) {
          console.error(`[VMD] notifyComplete ✗ 网络异常: ${e.message}`)
          return { success: false }
        }
      }

      console.error(`[VMD] notifyComplete ✗ 超过${maxRounds}轮补传仍失败: ${file.name}`)
      return { success: false }
    },

    _updateProgress(u, totalChunks, startTime, lastBytes, fileName) {
      u.uploadedChunks = this._getSavedChunks(fileName).length || u.uploadedChunks + 1
      u.percent = Math.min(99, Math.round((u.uploadedChunks / totalChunks) * 100))

      const elapsed = (Date.now() - startTime) / 1000
      if (elapsed > 0.5) {
        const thisBytes = this.chunkSize * u.uploadedChunks
        u.speed = Math.round((thisBytes - (lastBytes || 0)) / elapsed / 1024)
        const remaining = totalChunks - u.uploadedChunks
        if (u.speed > 0) {
          const sec = Math.round(remaining * this.chunkSize / 1024 / u.speed)
          u.remaining = sec > 60 ? `${Math.floor(sec / 60)}分${sec % 60}秒` : `${sec}秒`
        }
      }
    },

    async _resumeFiles(files) {
      for (const file of files) {
        if (!this.paused) {
          await this._uploadFile(file)
        }
      }
      this.uploading = false
    },

    // ========== localStorage 断点续传 ==========
    _getSavedChunks(fileName) {
      try {
        const store = JSON.parse(localStorage.getItem('vmd_pending_uploads') || '{}')
        return store[fileName] || []
      } catch { return [] }
    },

    _markChunkDone(fileName, idx) {
      try {
        const store = JSON.parse(localStorage.getItem('vmd_pending_uploads') || '{}')
        const arr = store[fileName] || []
        if (!arr.includes(idx)) arr.push(idx)
        store[fileName] = arr
        localStorage.setItem('vmd_pending_uploads', JSON.stringify(store))
      } catch { /* 忽略 */ }
    },

    _isChunkDone(fileName, idx) {
      return this._getSavedChunks(fileName).includes(idx)
    },

    clearSavedChunks(fileName) {
      try {
        const store = JSON.parse(localStorage.getItem('vmd_pending_uploads') || '{}')
        delete store[fileName]
        localStorage.setItem('vmd_pending_uploads', JSON.stringify(store))
      } catch { /* ignore */ }
    },

    checkPendingUploads() {
      try {
        const store = JSON.parse(localStorage.getItem('vmd_pending_uploads') || '{}')
        const names = Object.keys(store)
        if (names.length > 0) {
          this.$message(`发现 ${names.length} 个未完成的上传，重新选择文件后可断点续传`, { duration: 4000 })
        }
      } catch { /* ignore */ }
    },

    saveHistory() {
      try {
        const history = JSON.parse(localStorage.getItem('vmd_upload_history') || '[]')
        history.unshift({
          time: new Date().toLocaleString(),
          count: this.files.length,
          userName: this.userInfo?.userName || ''
        })
        if (history.length > 20) history.length = 20
        localStorage.setItem('vmd_upload_history', JSON.stringify(history))
      } catch { /* ignore */ }
    },

    /**
     * ★ 生成唯一上传ID（UUID v4）
     *    替代旧的 _genUploadId（基于文件名+日期+hash，同名文件产生相同ID导致碰撞）。
     *    使用浏览器原生 crypto.randomUUID()，浏览器不支持时回退到时间戳+随机数。
     *    @returns {string} 全局唯一的上传ID
     */
    _genUUID() {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID()
      }
      // Fallback for older browsers
      const ts = Date.now().toString(36)
      const randA = Math.random().toString(36).substr(2, 9)
      const randB = Math.random().toString(36).substr(2, 5)
      return `${ts}-${randA}-${randB}`
    },

    /**
     * ★【已弃用】确定性上传ID（文件名+日期+hash）
     *    保留用于极端降级场景：当 fileIdMap 未命中时作为最终 fallback
     */
    _genUploadId(fileName) {
      const clean = fileName.replace(/[_ ]/g, '')
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      let hash = 0
      for (let i = 0; i < clean.length; i++) {
        hash = ((hash << 5) - hash) + clean.charCodeAt(i)
        hash |= 0
      }
      const shortHash = Math.abs(hash).toString(36).substr(0, 6)
      return `${clean}_${datePart}_${shortHash}`
    },

    _sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    /**
     * ★ 秒传：使用 Web Crypto API 计算文件 SHA-256
     * 比 JS MD5 库快 10x+，大文件友好
     * @returns {string|null} 十六进制哈希字符串，失败返回 null
     */
    async _computeFileHash(file) {
      try {
        const buffer = await file.arrayBuffer()
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      } catch (e) {
        console.warn('[VMD] 文件哈希计算失败:', e)
        return null
      }
    }
  },

  beforeDestroy() {
    if (this._connHandler) this._connHandler()
    if (this.uploading) this.pauseAll()
  }
}
</script>

<style lang="scss" scoped>
.vmd-upload-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 30px;
  -webkit-overflow-scrolling: touch;
}

// 初始化加载遮罩
.init-loading {
  position: fixed;
  inset: 0;
  background: #f5f7fa;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.init-spinner {
  text-align: center;
  i {
    font-size: 40px;
    color: #409eff;
  }
  p {
    margin-top: 16px;
    font-size: 14px;
    color: #909399;
  }
}

.upload-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  .back-btn {
    font-size: 22px;
    color: #333;
    margin-right: 12px;
    cursor: pointer;
  }
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
}

.section {
  margin: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  .summary {
    margin-left: auto;
    font-size: 13px;
    color: #909399;
    font-weight: 400;
  }
}

.identity-form {
  margin-bottom: 8px;
}

.identity-pending {
  color: #909399;
  font-size: 13px;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;

  .el-icon-loading {
    font-size: 14px;
    color: #409eff;
  }
}

.identity-info {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.select-area {
  border: 2px dashed #dcdfe6;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  color: #909399;
  transition: border-color .3s;
  cursor: pointer;
  &:active {
    border-color: #409eff;
    background: #ecf5ff;
  }
  .select-icon {
    font-size: 32px;
    color: #c0c4cc;
  }
  p {
    margin: 4px 0 0;
    font-size: 13px;
  }
  .hint {
    font-size: 12px;
    color: #c0c4cc;
  }
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.preview-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1;
  background: #f0f2f5;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .file-size {
    position: absolute;
    bottom: 2px;
    left: 2px;
    background: rgba(0,0,0,0.6);
    color: #fff;
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 4px;
  }
  .remove-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    background: rgba(255,0,0,0.7);
    color: #fff;
    font-size: 12px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

.btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.progress-item {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  .file-name {
    font-size: 13px;
    color: #303133;
    word-break: break-all;
    flex: 1;
    margin-right: 8px;
  }
  .status {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
    &.done { background: #e8f5e9; color: #4caf50; }
    &.uploading { background: #e3f2fd; color: #2196f3; }
    &.failed { background: #fce4ec; color: #f44336; }
    &.pending { background: #f5f5f5; color: #9e9e9e; }
  }
}

.progress-info {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
}

.network-status {
  margin-top: 8px;
  font-size: 12px;
  color: #606266;
}

.compress-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.45);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.compress-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px;
  text-align: center;
  width: 260px;
  .el-icon-loading {
    font-size: 36px;
    color: #409eff;
    margin-bottom: 12px;
  }
  p {
    margin: 0 0 8px;
    font-size: 14px;
    color: #303133;
  }
  .small {
    font-size: 12px;
    color: #909399;
  }
}
</style>
